"use client";

import { useEffect } from "react";

export type StatusBarStyle = "light" | "dark";

/**
 * Sets the OS status bar style for the current page.
 *
 * On web/PWA, writes `<meta name="theme-color">` and
 * `<meta name="apple-mobile-web-app-status-bar-style">` so the browser/PWA chrome
 * matches the page's intent. On Capacitor (when shells land), the same hook will
 * call `StatusBar.setStyle()` and `StatusBar.setBackgroundColor()` via the plugin.
 *
 * Capacitor detection is feature-gated — web bundles tree-shake the native code
 * out and the hook becomes a meta-tag writer.
 */
export function useStatusBar(style: StatusBarStyle = "dark", color = "#ffffff"): void {
  useEffect(() => {
    if (typeof document === "undefined") return;

    const themeColor = ensureMeta("theme-color");
    const previousThemeColor = themeColor.getAttribute("content") ?? "";
    themeColor.setAttribute("content", color);

    const iosStyle = ensureMeta("apple-mobile-web-app-status-bar-style");
    const previousIosStyle = iosStyle.getAttribute("content") ?? "";
    iosStyle.setAttribute("content", style === "light" ? "black-translucent" : "default");

    return () => {
      themeColor.setAttribute("content", previousThemeColor);
      iosStyle.setAttribute("content", previousIosStyle);
    };
  }, [style, color]);
}

function ensureMeta(name: string): HTMLMetaElement {
  let element = document.head.querySelector<HTMLMetaElement>(`meta[name="${name}"]`);
  if (!element) {
    element = document.createElement("meta");
    element.setAttribute("name", name);
    document.head.appendChild(element);
  }
  return element;
}
